import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, ArrowRight, Activity, Package, Wallet, UserCheck, BarChart3 } from "lucide-react";
import Reveal from "../components/Reveal";
import { ASSETS } from "../lib/brand";
import CTAStrip from "../components/CTAStrip";

const ACCENT = "#7C3AED";

const CARDS = [
  { icon: Activity, title: "Real-Time Business Monitoring", items: ["Live sales tracking", "Daily collection reports", "Business performance dashboard", "Branch-wise sales visibility"] },
  { icon: Package, title: "Stock Monitoring", items: ["Current stock availability", "Fast-moving items", "Low stock alerts", "Inventory valuation"] },
  { icon: Wallet, title: "Financial Monitoring", items: ["Customer ledger reports", "Supplier ledger reports", "Outstanding balances", "Collection tracking"] },
  { icon: UserCheck, title: "User Activity Tracking", items: ["Sales cancellation tracking", "Bill editing logs", "User login history", "Activity audit reports"] },
];

const BENEFITS = [
  "Monitor business from anywhere",
  "Increased operational transparency",
  "Faster decision making",
  "Better control over employees & operations",
];

export default function DigiposInsight() {
  return (
    <div data-testid="digipos-insight-page">
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: `linear-gradient(135deg, #0B2545 0%, #1A1A60 50%, ${ACCENT} 100%)` }}>
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
                <Link to="/digipos" className="hover:text-white">DigiPOS</Link><ChevronRight className="h-3 w-3" />
                <span className="text-white">Insight</span>
              </div>
              <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> For DigiPOS Zen Users</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">DigiPOS Insight</h1>
              <p className="font-display text-xl mt-2" style={{ color: "#D8C5FF" }}>Business Monitoring & Owner Dashboard</p>
              <p className="text-white/75 mt-5 max-w-xl">
                An advanced mobile application designed exclusively for DigiPOS Zen users. Get real-time visibility into daily operations from anywhere — whether you&apos;re travelling, in meetings or working remotely.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="insight-demo-btn" className="gi-btn-primary">Request Demo <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/digipos/zen" className="gi-btn-ghost">View Zen</Link>

                <button
                                className="gi-btn-ghost"
                                data-testid="zen-brochure-btn"
                                onClick={() => {
                                  window.open("https://play.google.com/store/apps/details?id=com.digipos.zeninsights&pcampaignid=web_share");
                                }}
                              >
                                Download App
                              </button>
              </div>
            </Reveal>
            
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative gi-float flex justify-center">
                <div className="absolute -inset-6 bg-gradient-to-br from-[#7C3AED]/40 to-transparent blur-3xl rounded-full" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white w-full max-w-md border border-white/10">
                  <img src={ASSETS.insights} alt="DigiPOS Insight" className="w-full h-auto block" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest" style={{ color: ACCENT }}>What You Get</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">Full visibility in your pocket</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 70}>
                <div className="gi-card gi-card-hover p-7 h-full" style={{ borderTop: `4px solid ${ACCENT}` }}>
                  <div className="h-12 w-12 rounded-xl grid place-items-center text-white mb-4" style={{ background: ACCENT }}>
                    <c.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-3">{c.title}</h3>
                  <ul className="space-y-1.5 text-sm text-[#1A1A2E]/85">
                    {c.items.map((it) => <li key={it} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: ACCENT }} />{it}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="gi-card p-8 md:p-12 text-center" style={{ borderTop: `4px solid ${ACCENT}` }}>
              <BarChart3 className="h-10 w-10 mx-auto mb-4" style={{ color: ACCENT }} />
              <h3 className="font-display font-bold text-2xl md:text-3xl">Management Dashboard</h3>
              <p className="text-[#6B7280] mt-3 max-w-xl mx-auto">
                KPI monitoring · Business summary reports · Sales comparison reports · Profitability insights — all in one screen.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {BENEFITS.map((b) => (
                <div key={b} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#DDE6F0]">
                  <span className="h-2 w-2 rounded-full mt-2 shrink-0" style={{ background: ACCENT }} />
                  <span className="text-sm font-medium">{b}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTAStrip heading="See your business — live, from anywhere" sub="Insight is included with most Zen packages. Talk to us to enable it." />
    </div>
  );
}
